// pages/admin/LiveTracking/LiveTracking.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { GoogleMap, LoadScript, Marker, Polyline } from "@react-google-maps/api";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import io from "socket.io-client";
import axios from "axios";
import styles from "./LiveTracking.module.scss";
import { DEV } from "../../../services/constants";

const LiveTracking = () => {
    const { id } = useParams();
    const history = useHistory();
    const toast = useRef(null);
    const mapRef = useRef(null);
    const socketRef = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [deliveryData, setDeliveryData] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [locationTrail, setLocationTrail] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 33.006257, lng: 73.15074 });
    const [mapZoom, setMapZoom] = useState(13);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    // Map settings
    const mapContainerStyle = {
        width: "100%",
        height: "100%",
    };

    const mapOptions = {
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        styles: [
            {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
            },
        ],
    };

    // STEP 1: Fetch initial data (delivery + location history) via API
    useEffect(() => {
        const loadInitialData = async () => {
            console.log("🔄 Loading initial data from API...");
            setLoading(true);

            try {
                // Fetch delivery data and location history in parallel
                await Promise.all([fetchDeliveryData(), fetchLocationHistory()]);

                console.log("✅ Initial data loaded successfully");
                setIsInitialDataLoaded(true);
            } catch (error) {
                console.error("❌ Error loading initial data:", error);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to load tracking data",
                        life: 3000,
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadInitialData();
        }
    }, [id]);

    // STEP 2: Initialize Socket.IO AFTER initial data is loaded
    useEffect(() => {
        if (isInitialDataLoaded) {
            console.log("🔌 Initializing socket connection...");
            initializeSocket();
        }

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                console.log("Socket disconnected on cleanup");
            }
        };
    }, [isInitialDataLoaded, id]);

    const fetchDeliveryData = async () => {
        try {
            console.log("📦 Fetching delivery data...");
            const response = await axios.get(`${DEV}/delivery/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.data.success) {
                const { delivery, stops } = response.data.data;
                setDeliveryData({ ...delivery, stops });
                console.log("✅ Delivery data loaded:", delivery.deliveryId);

                // Set initial map center
                if (delivery.origin) {
                    setMapCenter({
                        lat: delivery.origin.latitude,
                        lng: delivery.origin.longitude,
                    });
                }
            }
        } catch (error) {
            console.error("❌ Error fetching delivery data:", error);
            throw error;
        }
    };

    const fetchLocationHistory = async () => {
        try {
            console.log("📍 Fetching location history from API...");
            const response = await axios.get(`${DEV}/delivery/${id}/locations`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.data.success) {
                const locations = response.data.data.map((loc) => ({
                    lat: loc.latitude,
                    lng: loc.longitude,
                    accuracy: loc.accuracy,
                    timestamp: loc.timestamp,
                    driverName: loc.driverName,
                }));

                console.log(`✅ Loaded ${locations.length} location points from history`);
                setLocationTrail(locations);

                // Set current location to latest from history
                if (locations.length > 0) {
                    const latest = locations[locations.length - 1];
                    setCurrentLocation(latest);
                    setMapCenter({ lat: latest.lat, lng: latest.lng });
                    setLastUpdate(new Date(latest.timestamp));
                    console.log("📍 Latest location set:", {
                        lat: latest.lat.toFixed(6),
                        lng: latest.lng.toFixed(6),
                        time: formatTime(latest.timestamp),
                    });
                } else {
                    console.log("ℹ️ No location history available");
                }
            }
        } catch (error) {
            console.error("❌ Error fetching location history:", error);
            // Don't throw - allow socket to work even if history fetch fails
        }
    };

    const initializeSocket = () => {
        const socketURL = process.env.REACT_APP_SOCKET_URL || "https://zazio-89059772666.us-central1.run.app/";
        const token = localStorage.getItem("token");

        console.log("🔌 Connecting to socket:", socketURL);

        socketRef.current = io(socketURL, {
            auth: { token },
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
        });

        socketRef.current.on("connect", () => {
            console.log("✅ Socket connected:", socketRef.current.id);
            setIsConnected(true);

            // Join delivery-specific room
            socketRef.current.emit("join-delivery", id);
            console.log(`📡 Joined delivery room: ${id}`);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Connected",
                    detail: "Live tracking active",
                    life: 2000,
                });
            }
        });

        socketRef.current.on("disconnect", (reason) => {
            console.log("❌ Socket disconnected:", reason);
            setIsConnected(false);

            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Disconnected",
                    detail: "Attempting to reconnect...",
                    life: 2000,
                });
            }
        });

        socketRef.current.on("reconnect", (attemptNumber) => {
            console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
            setIsConnected(true);

            // Rejoin delivery room after reconnection
            socketRef.current.emit("join-delivery", id);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Reconnected",
                    detail: "Live tracking resumed",
                    life: 2000,
                });
            }
        });

        // Listen for REAL-TIME location updates
        socketRef.current.on(`delivery-location-${id}`, (data) => {
            console.log("📍 LIVE location update received:", {
                lat: data.latitude.toFixed(6),
                lng: data.longitude.toFixed(6),
                time: formatTime(data.timestamp),
                driver: data.driverName,
            });

            const newLocation = {
                lat: data.latitude,
                lng: data.longitude,
                accuracy: data.accuracy,
                timestamp: data.timestamp,
                driverName: data.driverName,
            };

            // Update current location
            setCurrentLocation(newLocation);
            setLastUpdate(new Date(data.timestamp));

            // Add to trail (append new point)
            setLocationTrail((prev) => {
                // Avoid duplicates by checking if last point is same
                const lastPoint = prev[prev.length - 1];
                if (lastPoint && lastPoint.lat === newLocation.lat && lastPoint.lng === newLocation.lng) {
                    console.log("⚠️ Duplicate location ignored");
                    return prev;
                }
                console.log(`✅ Added point to trail (total: ${prev.length + 1})`);
                return [...prev, newLocation];
            });

            // Update map center to follow driver
            setMapCenter({ lat: data.latitude, lng: data.longitude });

            if (toast.current) {
                toast.current.show({
                    severity: "info",
                    summary: "Location Updated",
                    detail: `${data.driverName} moved`,
                    life: 1500,
                });
            }
        });

        socketRef.current.on("connect_error", (error) => {
            console.error("❌ Socket connection error:", error.message);
        });

        socketRef.current.on("reconnect_error", (error) => {
            console.error("❌ Socket reconnection error:", error.message);
        });

        socketRef.current.on("reconnect_failed", () => {
            console.error("❌ Socket reconnection failed");
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Connection Failed",
                    detail: "Unable to establish real-time connection",
                    life: 5000,
                });
            }
        });
    };

    const handleCenterOnDriver = () => {
        if (currentLocation) {
            console.log("🎯 Centering map on driver");
            setMapCenter({ lat: currentLocation.lat, lng: currentLocation.lng });
            setMapZoom(16);
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "No Location",
                    detail: "Driver location not available",
                    life: 2000,
                });
            }
        }
    };

    const handleFitAllMarkers = () => {
        if (!isMapLoaded) {
            console.log("⚠️ Map not loaded yet");
            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Map Loading",
                    detail: "Please wait for map to load",
                    life: 2000,
                });
            }
            return;
        }

        if (mapRef.current && locationTrail.length > 0) {
            console.log("🗺️ Fitting all markers on map");
            const bounds = new window.google.maps.LatLngBounds();

            // Include all trail points
            locationTrail.forEach((location) => {
                bounds.extend(new window.google.maps.LatLng(location.lat, location.lng));
            });

            // Include delivery stops
            if (deliveryData?.stops) {
                deliveryData.stops.forEach((stop) => {
                    bounds.extend(new window.google.maps.LatLng(stop.latitude, stop.longitude));
                });
            }

            mapRef.current.fitBounds(bounds);
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "No Data",
                    detail: "No location data to display",
                    life: 2000,
                });
            }
        }
    };

    const formatTime = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const formatTimeSince = (date) => {
        if (!date) return "N/A";
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    if (loading) {
        return (
            <div className={styles.liveTracking}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingContent}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: "3rem", color: "#8b5cf6" }} />
                        <p>Loading tracking data...</p>
                    </div>
                </div>
            </div>
        );
    }

    console.log(deliveryData, locationTrail);

    return (
        <div className={styles.liveTracking}>
            <Toast ref={toast} />

            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Button icon="pi pi-arrow-left" rounded text onClick={() => history.goBack()} className={styles.backButton} />
                    <div className={styles.headerInfo}>
                        <h1>Live Tracking</h1>
                        <p>
                            Delivery #{deliveryData?.deliveryId} • {deliveryData?.driverName}
                        </p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <div className={styles.connectionStatus}>
                        <div className={`${styles.statusDot} ${isConnected ? styles.connected : styles.disconnected}`} />
                        <span>{isConnected ? "Live" : "Offline"}</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className={styles.content}>
                {/* Map Container */}
                <div className={styles.mapContainer}>
                    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={mapZoom}
                            options={mapOptions}
                            onLoad={(map) => {
                                mapRef.current = map;
                                setIsMapLoaded(true);
                                console.log("🗺️ Map loaded successfully");
                            }}
                        >
                            {/* Only render markers if map is loaded */}
                            {isMapLoaded && (
                                <>
                                    {/* Location Trail Polyline */}
                                    {locationTrail.length > 1 && (
                                        <Polyline
                                            path={locationTrail}
                                            options={{
                                                strokeColor: "#8b5cf6",
                                                strokeOpacity: 0.8,
                                                strokeWeight: 4,
                                                geodesic: true,
                                            }}
                                        />
                                    )}

                                    {/* Trail Markers (faded) - All except last */}
                                    {locationTrail.slice(0, -1).map((location, index) => (
                                        <Marker
                                            key={`trail-${index}`}
                                            position={{ lat: location.lat, lng: location.lng }}
                                            icon={{
                                                path: window.google.maps.SymbolPath.CIRCLE,
                                                scale: 5,
                                                fillColor: "#8b5cf6",
                                                fillOpacity: 0.4,
                                                strokeColor: "#7c3aed",
                                                strokeWeight: 1,
                                            }}
                                            title={`${formatTime(location.timestamp)}`}
                                        />
                                    ))}

                                    {/* Current Location Marker (animated) */}
                                    {currentLocation && (
                                        <Marker
                                            position={{ lat: currentLocation.lat, lng: currentLocation.lng }}
                                            icon={{
                                                url:
                                                    "data:image/svg+xml;charset=UTF-8," +
                                                    encodeURIComponent(`
                                                        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                                            <circle cx="20" cy="20" r="18" fill="#8b5cf6" stroke="#ffffff" stroke-width="3"/>
                                                            <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                                                        </svg>
                                                    `),
                                                scaledSize: new window.google.maps.Size(40, 40),
                                                anchor: new window.google.maps.Point(20, 20),
                                            }}
                                            title={`${currentLocation.driverName} - ${formatTime(currentLocation.timestamp)}`}
                                            animation={window.google.maps.Animation.BOUNCE}
                                        />
                                    )}

                                    {/* Delivery Stops */}
                                    {deliveryData?.stops?.map((stop, index) => (
                                        <Marker
                                            key={stop._id}
                                            position={{ lat: stop.latitude, lng: stop.longitude }}
                                            label={{
                                                text: `${index + 1}`,
                                                color: "#ffffff",
                                                fontWeight: "bold",
                                            }}
                                            icon={{
                                                url: stop.status === "Delivered" ? "http://maps.google.com/mapfiles/ms/icons/green-dot.png" : stop.status === "Incomplete" ? "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png" : "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                                            }}
                                            title={`Stop ${index + 1}: ${stop.clientName} (${stop.status})`}
                                        />
                                    ))}
                                </>
                            )}
                        </GoogleMap>
                    </LoadScript>

                    {/* Map Controls */}
                    <div className={styles.mapControls}>
                        <Button icon="pi pi-crosshairs" rounded className={styles.controlButton} onClick={handleCenterOnDriver} tooltip="Center on Driver" tooltipOptions={{ position: "left" }} disabled={!currentLocation || !isMapLoaded} />
                        <Button icon="pi pi-map" rounded className={styles.controlButton} onClick={handleFitAllMarkers} tooltip="Fit All" tooltipOptions={{ position: "left" }} disabled={locationTrail.length === 0 || !isMapLoaded} />
                    </div>
                </div>

                {/* Info Sidebar */}
                <div className={styles.sidebar}>
                    {/* Driver Info Card */}
                    <Card className={styles.infoCard}>
                        <div className={styles.cardHeader}>
                            <i className="pi pi-user" />
                            <h3>Driver Information</h3>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Name</span>
                                <span className={styles.value}>{locationTrail?.[0]?.driverName || "N/A"}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Status</span>
                                <span className={styles.value}>
                                    <span className={styles.statusBadge}>{deliveryData?.status}</span>
                                </span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Last Update</span>
                                <span className={styles.value}>{lastUpdate ? formatTimeSince(lastUpdate) : "N/A"}</span>
                            </div>
                        </div>
                    </Card>

                    {/* Current Location Card */}
                    <Card className={styles.infoCard}>
                        <div className={styles.cardHeader}>
                            <i className="pi pi-map-marker" />
                            <h3>Current Location</h3>
                        </div>
                        <div className={styles.cardBody}>
                            {currentLocation ? (
                                <>
                                    <div className={styles.infoRow}>
                                        <span className={styles.label}>Latitude</span>
                                        <span className={styles.value}>{currentLocation.lat.toFixed(6)}</span>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.label}>Longitude</span>
                                        <span className={styles.value}>{currentLocation.lng.toFixed(6)}</span>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.label}>Accuracy</span>
                                        <span className={styles.value}>{currentLocation.accuracy?.toFixed(1) || "N/A"} m</span>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.label}>Time</span>
                                        <span className={styles.value}>{formatTime(currentLocation.timestamp)}</span>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.noData}>
                                    <i className="pi pi-info-circle" />
                                    <p>No location data available</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Location Trail Stats */}
                    <Card className={styles.infoCard}>
                        <div className={styles.cardHeader}>
                            <i className="pi pi-chart-line" />
                            <h3>Tracking Stats</h3>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Trail Points</span>
                                <span className={styles.value}>{locationTrail.length}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Total Stops</span>
                                <span className={styles.value}>{deliveryData?.stops?.length || 0}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Delivered</span>
                                <span className={styles.value}>{deliveryData?.stops?.filter((s) => s.status === "Delivered").length || 0}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>Remaining</span>
                                <span className={styles.value}>{deliveryData?.stops?.filter((s) => s.status === "Not Delivered").length || 0}</span>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default LiveTracking;
